import { downloadVideo } from 'lib/api';
import { useVideoHotspots } from 'lib/api/videos/getVideoHotspots';
import { useGetVideoLinks } from 'lib/api/videos/getVideoLinks';
import { TableContextProvider, useTableContext } from 'lib/components';
import { HelpDesk } from 'lib/components/helpDesk';
import { EHelpDesk } from 'lib/components/helpDesk/utils';
import { Gap, HeaderWrapper } from 'lib/components/styles/layout';
import {
  ParagraphNormalBold,
  ParagraphSmall,
} from 'lib/components/styles/typography';
import { useAuth } from 'lib/context';
import { theme } from 'lib/style';
import {
  checkIfFeatureIsEnabled,
  productFeature,
} from 'lib/utils/productFeature';
import * as React from 'react';
import { Button, Tooltip } from 'react-covideo-common';
import { BsFillBellFill, BsFillBellSlashFill } from 'react-icons/bs';
import { MdFileDownload } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components/macro';
import { usePageContext } from '../../../../PageContext';
import {
  CardStatsExitLinks,
  CardStatsHotspot,
  CardStatsViewers,
} from '../components';
import { ItemsWrapper, VideoEditorTitle } from '../styles/layout';
import SocialInsights from './socialInsights/SocialInsights';
import { useUpdateVideoViewNotificationsMutation } from 'lib/api/videos/useUpdateVideoViewNotificationsMutation';
import { useCDK } from 'lib/hooks/useCdk';
import { useTranslation } from 'react-i18next';
import { TabGroup, TabGroupItem } from 'lib/components/tabs/TabGroup';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import SendReportModal from 'lib/components/modal/SendReportModal';
import { useSendVideoStatsLinksReport } from 'lib/api/reports/useSendVideoStatsLinksReport';
import { useVideoMoveCopy } from 'lib/api/videos/useVideoMoveCopy';

const Stats = styled.div`
  box-shadow: 0 4px 12px 0 rgba(29, 30, 36, 0.04);
  border: solid 1px #f7f8fa;
  display: flex;
  padding: 16px 24px 16px 16px;
  align-items: center;
  justify-content: space-between;
`;
const StatsContainer = styled.div`
  width: 100%;
  ${theme.mediaQueryMinWidth.md} {
    width: 282px;
  }
`;

const ChartContainer = styled.div`
  position: relative;
  font-size: ${theme.fontSizes.xs};
  margin-top: 16px;
  .recharts-cartesian-grid {
    border-radius: 4px;
    box-shadow: 0 4px 12px 0 rgba(29, 30, 36, 0.04);
    border: solid 1px #f7f8fa;
  }
  ${theme.mediaQueryMinWidth.md} {
    margin-top: 0;
  }
  text {
    fill: ${theme.palette.label};
  }
`;
const NoDataContainer = styled.div`
  position: absolute;
  bottom: 60px;
  left: 60px;
`;

type Props = {
  video: any;
  videoId: string;
  view?: string;
  showActionsButtons?: boolean;
  from?: string;
  to?: string;
  allowVideoResend?: boolean;
};

const msToTime = (s: number) => {
  if (isNaN(s)) {
    return '00:00';
  }
  const ms = s % 1000;
  s = (s - ms) / 1000;
  const secs = s % 60;
  s = (s - secs) / 60;
  const seconds = secs >= 10 ? secs : '0' + secs;
  const mins = s % 60;
  let minutes = mins >= 10 ? mins : '0' + mins;
  return minutes + ':' + seconds;
};

export const Analytics = (props: Props) => {
  const {
    video,
    showActionsButtons,
    videoId,
    from,
    to,
    allowVideoResend = true,
  } = props;
  const { t } = useTranslation();

  const { userData } = useAuth();
  const { isCdkUser } = useCDK();
  const { pathname, search } = useLocation();
  const tableContext = useTableContext();
  const { folder } = usePageContext();

  const [isSocialFeatureAccessible, setIsSocialFeatureAccessible] =
    useState(false);
  const [disableNotificationButtons, setDisableNotificationButtons] =
    useState(true);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const isCompany = folder?.name === 'Company' ? 1 : 0;
  const tab = new URLSearchParams(search).get('tab');
  const isRecipientsActive = tab === 'recipients';
  const isAnalytics = pathname.includes('analytics');

  const { mutateAsync, isLoading: isUpdatingNotifications } =
    useUpdateVideoViewNotificationsMutation();
  const handleUpdateNotification = async (notify: boolean) => {
    await mutateAsync({ videoId: Number(videoId), notify });
  };

  const { mutateAsync: sendReport, isLoading: isSending } =
    useSendVideoStatsLinksReport({
      onSuccess: () => setIsSendModalOpen(false),
    });

  const { mutateAsync: moveVideo, isLoading: isMoving } = useVideoMoveCopy();

  const {
    data: hotspotsDataResponse,
    isLoading: loading,
    isError: error,
  } = useVideoHotspots({
    videoId: Number(videoId),
    params: {
      size: 500,
      page: 0,
      search: tableContext.search,
      from,
      to,
      isCompany: isCompany,
    },
    onCountChange: tableContext.setCount,
    userData: userData,
  });
  let hotspotsResponse = hotspotsDataResponse || {
    hotspots: [],
    chart: [],
    views: 0,
    viewPercentage: '',
  };

  const { data: linksResponse } = useGetVideoLinks({
    videoId: videoId,
    params: {
      size: 500,
      page: 0,
      search: tableContext.search,
      isCompany: isCompany,
    },
    onCountChange: tableContext.setCount,
  });

  const ctaResponse = linksResponse || { links: [] };

  const tabItems: TabGroupItem[] = useMemo(() => {
    return [
      {
        value: 'hotspot',
        title: t('Hotspot'),
        component: (
          <TableContextProvider>
            <CardStatsHotspot
              loading={loading}
              hotspots={hotspotsResponse.hotspots}
              error={error}
            />
          </TableContextProvider>
        ),
      },
      {
        value: 'recipients',
        title: t('Recipients'),
        component: (
          <CardStatsViewers
            videoId={videoId}
            setDisableNotificationButtons={setDisableNotificationButtons}
            allowVideoResend={allowVideoResend}
          />
        ),
      },
      {
        value: 'cta',
        title: t('Calls-to-action'),
        component: (
          <TableContextProvider>
            <CardStatsExitLinks
              ctas={ctaResponse.links}
              onSendButtonClick={() => setIsSendModalOpen(true)}
            />
          </TableContextProvider>
        ),
      },

      ...(!isCdkUser && isSocialFeatureAccessible
        ? [
            {
              value: 'social',
              title: t('Social'),
              component: <SocialInsights videoId={videoId} />,
            },
          ]
        : []),
    ];
  }, [
    t,
    isCdkUser,
    isSocialFeatureAccessible,
    loading,
    hotspotsResponse.hotspots,
    error,
    videoId,
    setDisableNotificationButtons,
    allowVideoResend,
    ctaResponse.links,
  ]);

  useEffect(() => {
    if (userData && userData.user && userData.user.packageDetails) {
      const userPackageDetails = userData.user.packageDetails;
      if (userPackageDetails.id) {
        const isAnalticsAllowed = checkIfFeatureIsEnabled(
          userData,
          productFeature.HOTSPOTS_AND_ANALYTICS
        );
        if (!isAnalticsAllowed) {
          window.location.href = '/upgrade-plan';
        }
      }

      setIsSocialFeatureAccessible(
        checkIfFeatureIsEnabled(userData, productFeature.FACEBOOK) ||
          checkIfFeatureIsEnabled(userData, productFeature.YOUTUBE)
      );
    }
  }, []);

  const handleCopyToLibrary = async (
    event: MouseEvent,
    videoId: number | string
  ) => {
    if (event && event.detail > 1) {
      return;
    }

    await moveVideo({
      videoId: videoId?.toString(),
      videoFolder: 'Primary',
      fromCompany: true,
      copy: true,
      successMessage: t('Video copied successfully'),
    });
  };

  const handleDownload = (
    event: MouseEvent,
    videoId: number | string,
    title: string
  ) => {
    if (event && event.detail > 1) {
      return;
    }
    downloadVideo(videoId.toString(), `${title}.mp4`);
  };

  const handleSend = async (recipients: string[]) => {
    await sendReport({
      recipients,
      videoId: Number(videoId),
    });
  };

  return (
    <>
      {isAnalytics && <HelpDesk name={EHelpDesk.ANALYTICS} />}
      <HeaderWrapper>
        <VideoEditorTitle>{video.title}</VideoEditorTitle>

        <Gap>
          {isRecipientsActive && (
            <>
              <Tooltip
                position={'bottom' as any}
                hoverable={true}
                extendStyles={{
                  popup: {
                    width: '165px',
                    left: '50%',
                    lineHeight: '24px',
                  },
                }}
                popup={
                  <div>
                    {t(
                      'You will be notified when the current recipients view this video.'
                    )}
                  </div>
                }
              >
                <Button
                  text={t('Enable Notifications')}
                  icon={<BsFillBellFill size={16} />}
                  disabled={
                    disableNotificationButtons || isUpdatingNotifications
                  }
                  onClick={() => {
                    handleUpdateNotification(true);
                  }}
                  variant='secondary'
                />
              </Tooltip>
              <Tooltip
                position={'bottom' as any}
                hoverable={true}
                extendStyles={{
                  popup: {
                    width: '165px',
                    left: '50%',
                    lineHeight: '24px',
                  },
                }}
                popup={
                  <div>
                    {t(
                      'You will no longer be notified when current recipients view this video.'
                    )}
                  </div>
                }
              >
                <Button
                  text={t('Disable Notifications')}
                  disabled={
                    disableNotificationButtons || isUpdatingNotifications
                  }
                  icon={<BsFillBellSlashFill size={16} />}
                  onClick={() => {
                    handleUpdateNotification(false);
                  }}
                  variant='destructive'
                />
              </Tooltip>
            </>
          )}
          {showActionsButtons && (
            <>
              <Button
                text={t('Download')}
                icon={<MdFileDownload size={20} />}
                onClick={e => {
                  handleDownload(
                    e as unknown as MouseEvent,
                    video?.id,
                    video.title
                  );
                }}
              />
              <Button
                text={t('Copy to Library')}
                loading={isMoving}
                disabled={isMoving}
                onClick={async (event: MouseEvent | SyntheticEvent) => {
                  await handleCopyToLibrary(event as MouseEvent, video?.id);
                }}
              />
            </>
          )}
        </Gap>
      </HeaderWrapper>
      {!loading && hotspotsResponse.hotspots && (
        <ItemsWrapper gap='60px'>
          <StatsContainer>
            <Stats>
              <ParagraphNormalBold>
                {hotspotsResponse.views}
              </ParagraphNormalBold>
              <ParagraphSmall>{t('Total Views')}</ParagraphSmall>
            </Stats>
            <Stats style={{ margin: '16px 0' }}>
              <ParagraphNormalBold>
                {hotspotsResponse.viewPercentage}%
              </ParagraphNormalBold>
              <ParagraphSmall>{t('Avg. Engagement')}</ParagraphSmall>
            </Stats>
            <Stats>
              <ParagraphNormalBold>
                {ctaResponse.links.length}
              </ParagraphNormalBold>
              <ParagraphSmall>{t('Total CTA Clicks')}</ParagraphSmall>
            </Stats>
          </StatsContainer>
          <ChartContainer>
            {!hotspotsResponse.hotspots.length && (
              <NoDataContainer>
                <ParagraphSmall>{t('No data to show.')}</ParagraphSmall>
              </NoDataContainer>
            )}
            <ComposedChart
              width={700}
              height={280}
              data={hotspotsResponse.chart}
            >
              <defs>
                <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                  <stop
                    offset='45%'
                    stopColor='rgba(0, 27, 83, 0.8)'
                    stopOpacity={0.5}
                  />
                  <stop
                    offset='95%'
                    stopColor='rgba(0, 27, 83, 0.3)'
                    stopOpacity={0.5}
                  />
                </linearGradient>
              </defs>
              <XAxis dataKey='x' tickFormatter={value => msToTime(value)} />
              <YAxis allowDecimals={false} width={30} />
              <CartesianGrid stroke={theme.palette.gray} />
              <Line
                type='monotone'
                unit='M'
                strokeLinecap='round'
                strokeWidth={1}
                dataKey='y'
                stroke='rgba(0, 27, 83, 1)'
                dot={false}
                legendType='none'
              />
              <Area
                type='monotone'
                dataKey='y'
                stroke={''}
                strokeWidth={2}
                fillOpacity={1}
                fill='url(#colorUv)'
              />
            </ComposedChart>
          </ChartContainer>
        </ItemsWrapper>
      )}

      <TabGroup items={tabItems} />

      {isSendModalOpen && (
        <SendReportModal
          onModalClose={() => setIsSendModalOpen(false)}
          onSendReport={handleSend}
          isSending={isSending}
        />
      )}
    </>
  );
};
